import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import useSWR from "swr";

import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

import format from "date-fns/format";
import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons/faQuestionCircle";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";

import CustomCard from "components/CustomCard";
import { Loader } from "components/customElements/loader";
import { entityUrl } from "components/EntityPageLayout";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Modal from "components/Modal";
import { NotificationContext } from "components/Notification/NotificationContext";
import ShareButton from "components/ShareButton";
import SignUpForm from "components/SignUpForm";
import useAuthentication from "hooks/useAuthentication";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import shortSecurity from "utils/shortSecurity";

import {
  BRIEFING_PILLAR_ORDER,
  BRIEFING_SUBSECTION_ORDER,
} from "./briefingPillarOrder";
import { BriefingIcon, SecurityTrendIcon } from "./icons";
import { renderSectionContent } from "./renderSectionContent";
import {
  asString,
  getSectionTone,
  isWithinOneMonth,
  toTitle,
} from "./sectionHelpers";
import { useTopInvestorQuestionsRequest } from "./useTopInvestorQuestionsRequest";

const TopInvestorQuestionsRequestLoader = dynamic(
  () => import("./TopInvestorQuestionsRequestLoader")
);
const MAX_POLLING_RETRY = 6;

type TopInvestorQuestionItem = {
  id: number;
  entity_id: number;
  entity_name: string;
  entity_ticker: string;
  status: "queued" | "processing" | "completed" | "failed";
  briefing_json?: Record<string, any>;
  error_message?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
};

type EntityPageTopInvestorQuestionsModalProps = {
  slug: string;
  isOpen: boolean;
  onClose?: () => void;
};

export const topInvestorQuestionsUrl = (entityId: number, limit = 1) => {
  const url = new ApiEndpoint("/api/v3/top-investor-questions");
  url.searchParams.append("entity_id", String(entityId));
  url.searchParams.append("limit", String(limit));

  return url.href;
};

const EntityPageTopInvestorQuestionsModal = ({
  slug,
  isOpen,
  onClose,
}: EntityPageTopInvestorQuestionsModalProps) => {
  const router = useRouter();
  const { authenticated } = useAuthentication();
  const { setNotification: _setNotification } = useContext(NotificationContext);
  const [_showStripeModal, toggleStripeModal] = useToggle();
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const closeModal = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    const nextQuery = { ...router.query };
    delete nextQuery["top-investor-questions"];
    delete nextQuery.item;

    router.replace({ pathname: router.pathname, query: nextQuery }, undefined, {
      shallow: true,
      scroll: false,
    });
  }, [onClose, router]);

  const { data: entity } = useSWR(isOpen ? entityUrl(slug) : null, baseFetcher);
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pollingRetryCountRef = useRef(0);

  const endpoint = entity?.id ? topInvestorQuestionsUrl(entity.id, 1) : null;

  const { data, mutate, isLoading, isValidating } = useSWR<
    TopInvestorQuestionItem[]
  >(endpoint, baseFetcher, {
    revalidateOnFocus: false,
    refreshInterval: (latestData) => {
      const hasPending =
        latestData?.some(
          (item) => item.status === "queued" || item.status === "processing"
        ) ?? false;

      if (!hasPending) {
        pollingRetryCountRef.current = 0;
        return 0;
      }

      if (pollingRetryCountRef.current >= MAX_POLLING_RETRY) {
        return 0;
      }

      pollingRetryCountRef.current += 1;
      return 30000;
    },
  });

  useEffect(() => {
    if (!isOpen) {
      pollingRetryCountRef.current = 0;
    }
  }, [isOpen]);

  const items = useMemo(() => {
    const source = data || [];

    return source.filter((item) =>
      isWithinOneMonth(item.created_at || item.updated_at)
    );
  }, [data]);

  const latestRecord = items[0];
  const hasPendingGeneration =
    !latestRecord ||
    latestRecord?.status === "queued" ||
    latestRecord?.status === "processing";

  const shouldShowDetails =
    authenticated && !isLoading && !hasPendingGeneration && !!latestRecord;

  useTopInvestorQuestionsRequest({
    entity,
    items,
    isLoading,
    isValidating,
    mutate,
    onRequireUpgrade: toggleStripeModal,
    shouldAutoGenerate: isOpen,
  });

  const briefingPillars = useMemo(
    () => latestRecord?.briefing_json?.briefing_pillars || {},
    [latestRecord?.briefing_json?.briefing_pillars]
  );
  const top10ProbingQuestions = useMemo(
    () => latestRecord?.briefing_json?.top_10_probing_questions || [],
    [latestRecord?.briefing_json?.top_10_probing_questions]
  );
  const investorCheatSheet = useMemo(
    () => latestRecord?.briefing_json?.investor_cheat_sheet || [],
    [latestRecord?.briefing_json?.investor_cheat_sheet]
  );

  const title = `${name || "Meeting Prep"}${
    shortedSecurity ? ` | ${shortedSecurity}` : ""
  }`;

  const updatedAt = latestRecord
    ? format(
        new Date(latestRecord.updated_at || latestRecord.created_at),
        "dd MMM yyyy"
      )
    : "recently";

  // Share button options
  const shareBtnOptions = useMemo(() => {
    const baseUrl = `${emberAppBaseUrl}/entities/${slug}`;
    const shareUrl = `${baseUrl}?top-investor-questions=true`;
    const title = `Check this out: Meeting Prep for ${name || slug}${
      shortedSecurity ? ` (${shortedSecurity})` : ""
    }`;

    return {
      email: {
        link: `mailto:?subject=⭐ Smartkarma | Meeting Prep&body=${title} ${shareUrl}`,
      },
      clipboard: {
        message: `${title} ${shareUrl}`,
      },
      privateMessage: {
        message: `Meeting Prep | ${
          name || slug
        }: <a href="${shareUrl}">${shareUrl}</a>`,
      },
      whatsapp: {
        link: `https://web.whatsapp.com/send?text=${encodeURIComponent(
          `${title} ${shareUrl}`
        )}`,
      },
      twitter: {
        link: `https://twitter.com/share?url=${encodeURIComponent(
          shareUrl
        )}&text=${encodeURIComponent(title)}&via=smartkarma`,
      },
      linkedIn: {
        link: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
          shareUrl
        )}&source=Smartkarma`,
      },
    };
  }, [name, shortedSecurity, slug]);

  // Export to PDF
  const handleExportPDF = useCallback(async () => {
    if (!latestRecord) {
      return;
    }

    setIsExportingPDF(true);

    try {
      const { downloadTopInvestorQuestionsPdf } = await import(
        "./topInvestorQuestionsPdf"
      );

      await downloadTopInvestorQuestionsPdf({
        title,
        entityName: name || slug,
        entityPageUrl: `${emberAppBaseUrl}/entities/${slug}?top-investor-questions=true`,
        shortedSecurity: shortedSecurity || undefined,
        sector: entity?.sector || undefined,
        country: entity?.country || undefined,
        updatedAt,
        probingQuestions: top10ProbingQuestions,
        briefingPillars,
        investorCheatSheet,
      });
    } catch (error) {
      console.error("Error exporting top investor questions PDF:", error);
    } finally {
      setIsExportingPDF(false);
    }
  }, [
    briefingPillars,
    entity?.country,
    entity?.sector,
    investorCheatSheet,
    latestRecord,
    name,
    shortedSecurity,
    slug,
    title,
    top10ProbingQuestions,
    updatedAt,
  ]);

  const panelCards = useMemo(() => {
    const orderedPillars = BRIEFING_PILLAR_ORDER as readonly string[];
    const allEntries = Object.entries(briefingPillars);
    const entries = [
      ...orderedPillars
        .filter((key) => key in briefingPillars)
        .map((key) => [key, briefingPillars[key]] as [string, any]),
      ...allEntries.filter(([key]) => !orderedPillars.includes(key)),
    ];
    const icons = ["trend", "clock", "shield", "alert"] as const;

    return entries.map(([pillarKey, pillarValue], index) => (
      <CustomCard
        key={pillarKey}
        noShadow
        className="mb-4 overflow-hidden rounded-[8px] border border-solid border-[#E8E8E8] p-0 shadow-[0_1px_4px_rgba(0,0,0,.06)]"
      >
        <div className="flex items-center gap-3 border-b border-[#E8E8E8] bg-[#EEEEEE] px-5 py-4 md:px-6">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4px] bg-[#E1F8F8] text-smart-teal">
            <BriefingIcon type={icons[index % icons.length]} />
          </span>
          <h3 className="m-0 text-[16px] font-medium leading-6 text-[#1A1A1A] md:text-[18px]">
            {toTitle(pillarKey)}
          </h3>
        </div>
        <div className="space-y-5 px-5 py-4 md:px-6 md:py-5">
          {typeof pillarValue === "object" &&
          pillarValue !== null &&
          !Array.isArray(pillarValue)
            ? (() => {
                const orderedSubsections = BRIEFING_SUBSECTION_ORDER[pillarKey];
                const rawSubEntries = Object.entries(
                  pillarValue as Record<string, any>
                );
                const subEntries = orderedSubsections?.length
                  ? [
                      ...orderedSubsections
                        .filter(
                          (k) => k in (pillarValue as Record<string, any>)
                        )
                        .map(
                          (k) =>
                            [k, (pillarValue as Record<string, any>)[k]] as [
                              string,
                              any
                            ]
                        ),
                      ...rawSubEntries.filter(
                        ([k]) => !orderedSubsections.includes(k)
                      ),
                    ]
                  : rawSubEntries;
                const isStreetNarrative =
                  pillarKey === "street_narrative_vs_reality";

                const subsectionNodes = subEntries.map(
                  ([subKey, subValue], subIndex) => {
                    const sectionTone = getSectionTone(subKey);
                    const isQuoteSection =
                      subKey === "killer_question" ||
                      subKey === "uncomfortable_question";

                    const renderOptions = {
                      ...(isStreetNarrative
                        ? {
                            noDottedLines: true,
                            forceBulletColor: "bg-[#1A1A1A]",
                          }
                        : {}),
                      ...(isQuoteSection
                        ? { noQuoteMargin: true, hideQuoteTitle: true }
                        : {}),
                    };

                    return (
                      <Fragment key={subKey}>
                        {isStreetNarrative && subIndex > 0 && (
                          <hr className="border-t border-[#E8E8E8]" />
                        )}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.04em] text-inactive-black">
                            <span>{sectionTone.title}</span>
                            {sectionTone.tag && (
                              <span
                                className={twMerge(
                                  "rounded-full px-2 py-[1px] text-[10px] font-semibold uppercase leading-none",
                                  sectionTone.tagClassName
                                )}
                              >
                                {sectionTone.tag}
                              </span>
                            )}
                          </div>
                          {renderSectionContent(
                            subKey,
                            subValue,
                            renderOptions
                          )}
                        </div>
                      </Fragment>
                    );
                  }
                );

                if (isStreetNarrative && investorCheatSheet?.length) {
                  return (
                    <>
                      {subsectionNodes}
                      <hr className="border-t border-[#E8E8E8]" />
                      <div className="space-y-3">
                        <div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-inactive-black">
                          Analyst Insights
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full border-collapse text-[13px]">
                            <thead>
                              <tr className="bg-[#F5F5F5] text-left text-[#404040]">
                                <th className="border-b border-[#E8E8E8] px-4 py-3 font-medium">
                                  Topic
                                </th>
                                <th className="border-b border-[#E8E8E8] px-4 py-3 font-medium">
                                  Consensus answer
                                </th>
                                <th className="border-b border-[#E8E8E8] px-4 py-3 font-medium">
                                  Analyst insight
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {investorCheatSheet.map(
                                (entry: any, idx: number) => (
                                  <tr
                                    key={`cheat-${idx}`}
                                    className="border-b border-[#E8E8E8] align-top text-[#404040] last:border-b-0 hover:bg-[#F5F5F5]"
                                  >
                                    <td className="w-[180px] px-4 py-3 font-medium text-[#1A1A1A]">
                                      {asString(entry?.topic)}
                                    </td>
                                    <td className="w-[250px] px-4 py-3 text-[#808080]">
                                      {asString(entry?.consensus_answer)}
                                    </td>
                                    <td className="px-4 py-3 text-[#1A1A1A]">
                                      {asString(entry?.analyst_insight)}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  );
                }

                return subsectionNodes;
              })()
            : renderSectionContent(pillarKey, pillarValue)}
        </div>
      </CustomCard>
    ));
  }, [briefingPillars, investorCheatSheet]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      className="md:!w-[720px] !max-w-none !overflow-hidden !rounded-[8px] !border !border-solid !border-[#E8E8E8] md:!p-0 md:!my-8 md:!h-[calc(100dvh-64px)] md:!max-h-[calc(100dvh-64px)]"
      overlayClassName="!overflow-hidden !bg-[rgba(42,42,42,.78)]"
      closeCTAClassName="!top-[12px] !right-[12px]"
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
        <div className="flex items-start justify-between gap-4 border-b border-[#E8E8E8] px-6 py-5 md:px-6 md:py-5 md:pr-[50px]">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.04em] text-[#808080]">
              <span className="h-1.5 w-1.5 rounded-full bg-smart-teal" />
              Meeting Prep
            </div>
            <h1 className="m-0 mb-1 text-[20px] font-bold leading-tight text-[#1A1A1A] md:text-[22px]">
              {title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#808080]">
              {shortedSecurity && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#E1F8F8] px-3 py-[2px] font-medium text-smart-teal">
                  <SecurityTrendIcon />
                  {shortedSecurity}
                </span>
              )}
              <span className="text-[#BFBFBF]">•</span>
              {entity?.sector && <span>{entity?.sector}</span>}
              {entity?.sector && entity?.country && (
                <span className="text-[#BFBFBF]">•</span>
              )}
              {entity?.country && <span>{entity?.country}</span>}
              <span className="text-[#BFBFBF]">•</span>
              <span>Prepared for Company Meeting</span>
              {shouldShowDetails && (
                <>
                  <span className="text-[#BFBFBF]">•</span>
                  <span>Updated - {updatedAt}</span>
                </>
              )}
            </div>
          </div>
          {shouldShowDetails && (
            <div
              className={twMerge(
                "absolute right-[10px] top-[65px] md:right-[54px] md:top-auto",
                "flex items-center gap-2 mt-[8px]"
              )}
            >
              <ShareButton
                shareKey={`top-investor-questions-${entity?.id}`}
                options={shareBtnOptions}
                noBorder
                columnView
                className="text-[16px] text-shady-gray hover:text-smart-teal"
                tooltipPlacement="bottom"
              />
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Download PDF Copy</Tooltip>}
              >
                <span className="inline-flex">
                  <button
                    onClick={handleExportPDF}
                    disabled={isExportingPDF}
                    className={twMerge(
                      "flex items-center justify-center w-9 h-9 rounded-full transition-colors",
                      "border-0 outline-none bg-transparent",
                      "text-[16px]",
                      isExportingPDF
                        ? "text-[#BFBFBF] cursor-not-allowed"
                        : "text-shady-gray hover:text-black"
                    )}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                </span>
              </OverlayTrigger>
            </div>
          )}
        </div>

        <div
          id="investor-questions-content"
          className="flex flex-col min-h-0 flex-1 overflow-hidden"
        >
          <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-4 md:px-6 md:py-5">
            {!authenticated ? (
              <SignUpForm type="insight-signup" />
            ) : isLoading || !latestRecord ? (
              <CustomCard
                noShadow
                className="mb-0 rounded-[8px] border border-solid border-[#E8E8E8] p-6 shadow-none"
              >
                <Loader numberOfItems={5} />
              </CustomCard>
            ) : hasPendingGeneration ? (
              <CustomCard
                noShadow
                className="mb-0 rounded-[8px] border border-solid border-[#E8E8E8] p-0 shadow-none"
              >
                <TopInvestorQuestionsRequestLoader />
              </CustomCard>
            ) : (
              <div className="space-y-4">
                {/* Probing Questions */}
                <CustomCard
                  noShadow
                  className="mb-4 overflow-hidden rounded-[8px] border border-solid border-[#E8E8E8] p-0 shadow-[0_1px_4px_rgba(0,0,0,.06)]"
                >
                  <div className="flex items-center gap-3 border-b border-[#E8E8E8] bg-[#EEEEEE] px-5 py-4 md:px-6">
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4px] bg-[#E1F8F8] text-smart-teal">
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        className="h-4 w-4"
                      />
                    </span>
                    <h3 className="m-0 text-[16px] font-medium leading-6 text-[#1A1A1A] md:text-[18px]">
                      Probing Questions
                    </h3>
                  </div>
                  <div className="space-y-5 px-5 py-4 md:px-6 md:py-5">
                    <ul className="m-0 list-none space-y-3 p-0">
                      {Array.isArray(top10ProbingQuestions) &&
                      top10ProbingQuestions.length ? (
                        top10ProbingQuestions.map(
                          (question: any, index: number) => (
                            <li
                              key={`${asString(question)}-${index}`}
                              className={twMerge(
                                "relative pl-6 text-[14px] leading-6 text-[#404040] md:text-[15px] md:leading-7"
                              )}
                            >
                              <span
                                className={twMerge(
                                  "absolute left-0 top-[5px] rounded-full bg-smart-teal",
                                  "flex h-4 w-4 flex-shrink-0 items-center justify-center text-[10px] text-white"
                                )}
                              >
                                {index + 1}
                              </span>
                              {asString(question)}
                            </li>
                          )
                        )
                      ) : (
                        <p className="m-0 text-[14px] leading-7 text-[#445164]">
                          No probing questions available.
                        </p>
                      )}
                    </ul>
                  </div>
                </CustomCard>

                {/* Briefing Pillars */}
                <div className="space-y-4">{panelCards}</div>
              </div>
            )}
          </div>
          <div className="border-0 border-t border-solid border-[#E8E8E8] bg-white px-4 py-3 md:px-6">
            <p className="m-0 text-[12px] leading-5 text-[#808080]">
              <strong>Disclaimer:</strong> This content is AI-generated and
              displayed for general informational purposes only. Please verify
              independently before use.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EntityPageTopInvestorQuestionsModal;
